import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
const MOCK_MODE = false; // Disable mock mode to use real APIs

const API_AI_BASE_URL =
  import.meta.env.VITE_API_AI_BASE_URL || "https://api.meetfinu.com";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increase timeout to 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
});

const apiAI = axios.create({
  baseURL: API_AI_BASE_URL,
  timeout: 30000, // Increase timeout to 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only redirect to login for 401 errors that are NOT from login attempts or profile updates
    if (error.response?.status === 401) {
      // Check if this is a login request
      const isLoginRequest =
        error.config?.url?.includes("/users/login") ||
        error.config?.url?.includes("/auth/login") ||
        error.config?.url?.includes("/users/signup");

      // Check if this is a profile update request
      const isProfileUpdate = error.config?.url?.includes("/users/me");

      // Only redirect if it's not a login attempt and not a profile update
      if (!isLoginRequest && !isProfileUpdate) {
        // Token expired or invalid - only redirect for non-login requests
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
        toast.error("Session expired. Please login again.");
      }
      // For login requests, let the error bubble up to be handled by the login form
    } else if (error.response?.status === 403) {
      toast.error("Access denied");
    } else if (error.response?.status >= 500) {
      toast.error("Server error. Please try again later.");
    } else if (error.code === "NETWORK_ERROR") {
      toast.error("Network error. Please check your connection.");
    }

    return Promise.reject(error);
  },
);

// Request interceptor to add auth token
apiAI.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem("user"));
    const token = user?.aiToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
apiAI.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // AI service 401 means aiToken is missing or expired.
      // Do NOT clear the main auth token or redirect — the user may
      // still be fully authenticated with the card backend.
      console.warn("AI service returned 401 — aiToken may be missing or expired");
    } else if (error.response?.status === 403) {
      toast.error("Access denied");
    } else if (error.response?.status >= 500) {
      toast.error("Server error. Please try again later.");
    } else if (error.code === "NETWORK_ERROR") {
      toast.error("Network error. Please check your connection.");
    }

    return Promise.reject(error);
  },
);

// API methods
export const apiService = {
  // Generic methods
  get: async (url, config = {}) => {
    const response = await api.get(url, config);
    return response.data;
  },

  /** GET returning raw blob plus optional filename from Content-Disposition. */
  getBlob: async (url, config = {}) => {
    const response = await api.get(url, {
      ...config,
      responseType: "blob",
    });
    const blob = response.data;
    const disposition = response.headers["content-disposition"];
    let filename = null;
    if (typeof disposition === "string") {
      const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
      if (utf8Match?.[1]) {
        try {
          filename = decodeURIComponent(utf8Match[1].trim());
        } catch {
          filename = utf8Match[1].trim();
        }
      } else {
        const match = /filename="?([^";\n]+)"?/i.exec(disposition);
        if (match?.[1]) filename = match[1].trim();
      }
    }
    return { blob, filename };
  },

  post: async (url, data, config = {}) => {
    const response = await api.post(url, data, config);
    return response.data;
  },

  // File upload method for multipart form data
  uploadFile: async (url, formData, config = {}) => {
    const response = await api.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // Longer timeout for file uploads
    });
    return response.data;
  },

  put: async (url, data, config = {}) => {
    const response = await api.put(url, data, config);
    return response.data;
  },

  patch: async (url, data, config = {}) => {
    const response = await api.patch(url, data, config);
    return response.data;
  },

  delete: async (url, config = {}) => {
    const response = await api.delete(url, config);
    return response.data;
  },

  // Search conversations with pagination
  searchConversations: async ({ search = "", limit = 10, page = 1 } = {}) => {
    const params = { limit, page };
    if (search) {
      params.search = search;
    }
    const response = await api.get("/chat/user-conversations/search", {
      params,
    });
    return response.data;
  },

  // User Profile methods
  updateUserProfile: async (profileData) => {
    const response = await api.put("/users/me", profileData);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post("/users/me/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // Subscription methods
  getSubscriptionStatus: async () => {
    const response = await api.get("/subscription/status");
    return response.data;
  },

  startTrial: async () => {
    const response = await api.post("/subscription/trial/start");
    return response.data;
  },

  // Wallet methods
  getWalletBalance: async () => {
    const response = await api.get("/api/wallet/balance");
    return response.data;
  },

  getWalletInfo: async () => {
    const response = await api.get("/api/wallet/info");
    return response.data;
  },

  signMessage: async (message) => {
    const response = await api.post("/api/wallet/sign-message", { message });
    return response.data;
  },

  backupWallet: async () => {
    const response = await api.post("/api/wallet/backup");
    return response.data;
  },

  // Rewards / FINU methods
  convertPointsToFinu: async ({ points, to_address, gas_price_gwei }) => {
    // Build request body, only include gas_price_gwei if it has a valid value
    const requestBody = {
      points,
      to_address,
    };

    if (gas_price_gwei !== undefined && gas_price_gwei !== null) {
      requestBody.gas_price_gwei = gas_price_gwei;
    }

    const response = await api.post(
      "/api/rewards/convert-points-to-finu",
      requestBody,
    );
    return response.data;
  },
  getFinuBalance: async (address) => {
    const response = await api.get("/api/rewards/finu-balance", {
      params: { address },
    });
    return response.data;
  },
  getTxStatus: async (txHash) => {
    const response = await api.get(`/api/rewards/transfer/${txHash}`);
    return response.data;
  },

  // Tokens on-chain
  getOnchainFinuBalance: async () => {
    const response = await api.get("/tokens/onchain-balance");
    return response.data;
  },
};

export const apiAIService = {
  // Generic methods
  get: async (url, config = {}) => {
    const response = await apiAI.get(url, config);
    return response.data;
  },

  /** GET returning raw blob plus optional filename from Content-Disposition. */
  getBlob: async (url, config = {}) => {
    const response = await apiAI.get(url, {
      ...config,
      responseType: "blob",
    });
    const blob = response.data;
    const disposition = response.headers["content-disposition"];
    let filename = null;
    if (typeof disposition === "string") {
      const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
      if (utf8Match?.[1]) {
        try {
          filename = decodeURIComponent(utf8Match[1].trim());
        } catch {
          filename = utf8Match[1].trim();
        }
      } else {
        const match = /filename="?([^";\n]+)"?/i.exec(disposition);
        if (match?.[1]) filename = match[1].trim();
      }
    }
    return { blob, filename };
  },

  post: async (url, data, config = {}) => {
    const response = await apiAI.post(url, data, config);
    return response.data;
  },

  // File upload method for multipart form data
  uploadFile: async (url, formData, config = {}) => {
    const response = await apiAI.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
      timeout: 30000, // Longer timeout for file uploads
    });
    return response.data;
  },

  put: async (url, data, config = {}) => {
    const response = await apiAI.put(url, data, config);
    return response.data;
  },

  patch: async (url, data, config = {}) => {
    const response = await apiAI.patch(url, data, config);
    return response.data;
  },

  delete: async (url, config = {}) => {
    const response = await apiAI.delete(url, config);
    return response.data;
  },

  // Search conversations with pagination
  searchConversations: async ({ search = "", limit = 10, page = 1 } = {}) => {
    const params = { limit, page };
    if (search) {
      params.search = search;
    }
    const response = await apiAI.get("/chat/user-conversations/search", {
      params,
    });
    return response.data;
  },

  // User Profile methods
  updateUserProfile: async (profileData) => {
    const response = await apiAI.put("/users/me", profileData);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await apiAI.post("/users/me/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // Subscription methods
  getSubscriptionStatus: async () => {
    const response = await apiAI.get("/subscription/status");
    return response.data;
  },

  startTrial: async () => {
    const response = await apiAI.post("/subscription/trial/start");
    return response.data;
  },

  // Wallet methods
  getWalletBalance: async () => {
    const response = await apiAI.get("/api/wallet/balance");
    return response.data;
  },

  getWalletInfo: async () => {
    const response = await apiAI.get("/api/wallet/info");
    return response.data;
  },

  signMessage: async (message) => {
    const response = await apiAI.post("/api/wallet/sign-message", { message });
    return response.data;
  },

  backupWallet: async () => {
    const response = await apiAI.post("/api/wallet/backup");
    return response.data;
  },

  // Rewards / FINU methods
  convertPointsToFinu: async ({ points, to_address, gas_price_gwei }) => {
    // Build request body, only include gas_price_gwei if it has a valid value
    const requestBody = {
      points,
      to_address,
    };

    if (gas_price_gwei !== undefined && gas_price_gwei !== null) {
      requestBody.gas_price_gwei = gas_price_gwei;
    }

    const response = await apiAI.post(
      "/api/rewards/convert-points-to-finu",
      requestBody,
    );
    return response.data;
  },
  getFinuBalance: async (address) => {
    const response = await apiAI.get("/api/rewards/finu-balance", {
      params: { address },
    });
    return response.data;
  },
  getTxStatus: async (txHash) => {
    const response = await apiAI.get(`/api/rewards/transfer/${txHash}`);
    return response.data;
  },

  // Tokens on-chain
  getOnchainFinuBalance: async () => {
    const response = await apiAI.get("/tokens/onchain-balance");
    return response.data;
  },
};

// Subscription service
export const subscriptionService = {
  getPlans: () => apiService.get("/subscription/plans"),
  createCheckoutSession: (planId) =>
    apiService.post("/subscription/create-checkout-session", {
      plan_id: planId,
    }),
  openBillingPortal: () => apiService.post("/subscription/portal", {}),
  cancel: () => apiService.post("/subscription/cancel", {}),
  status: () => apiService.get("/subscription/status"),
};

export default api;
